/**
 * Servicio base de API
 * Maneja las peticiones HTTP y la configuración común
 */

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

export interface ApiError {
  message: string;
  status?: number;
  errors?: Record<string, string[]>;
}

export class ApiService {
  private baseURL: string;
  private tokenGetter: (() => string | null) | null = null;
  private logoutCallback: (() => void) | null = null;

  constructor(baseURL: string = API_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Establece un callback para manejar el logout cuando el token es inválido
   */
  setLogoutCallback(callback: () => void): void {
    this.logoutCallback = callback;
  }

  /**
   * Maneja errores de autenticación (401)
   * Limpia el almacenamiento y redirige al login
   */
  private handleUnauthorized(): void {
    // Limpiar almacenamiento
    const oidcKeys = Object.keys(localStorage).filter(key => key.startsWith('oidc.user:'));
    oidcKeys.forEach(key => localStorage.removeItem(key));
    localStorage.removeItem('auth_token');
    localStorage.removeItem('access_token');
    sessionStorage.clear();

    // Ejecutar callback de logout si está configurado
    if (this.logoutCallback) {
      this.logoutCallback();
    } else {
      // Fallback: redirigir directamente
      window.location.href = '/';
    }
  }

  /**
   * Establece una función para obtener el token de autenticación
   * Esto permite que el token se obtenga directamente desde el contexto de React
   */
  setTokenGetter(getter: () => string | null): void {
    this.tokenGetter = getter;
  }

  /**
   * Obtiene el token de autenticación del usuario actual
   * Primero intenta usar el tokenGetter si está configurado,
   * luego busca en el almacenamiento de react-oidc-context
   * Si no hay token, espera hasta 3 segundos antes de retornar null
   */
  private async getAuthToken(): Promise<string | null> {
    const getTokenFromStorage = (): string | null => {
      // Si hay un tokenGetter configurado (desde el contexto de React), usarlo primero
      if (this.tokenGetter) {
        const token = this.tokenGetter();
        if (token) {
          return token;
        }
      }

      // react-oidc-context almacena el usuario en localStorage con una clave específica
      const oidcKeys = Object.keys(localStorage).filter(key => key.startsWith('oidc.user:'));

      if (oidcKeys.length > 0) {
        try {
          const userData = localStorage.getItem(oidcKeys[0]);
          if (userData) {
            const parsed = JSON.parse(userData);
            const token = parsed.access_token || null;
            if (token) {
              return token;
            }
          }
        } catch (error) {
          // Error parsing OIDC user data
        }
      }

      // Buscar en sessionStorage también
      const oidcKeysSession = Object.keys(sessionStorage).filter(key => key.startsWith('oidc.user:'));
      if (oidcKeysSession.length > 0) {
        try {
          const userData = sessionStorage.getItem(oidcKeysSession[0]);
          if (userData) {
            const parsed = JSON.parse(userData);
            const token = parsed.access_token || null;
            if (token) return token;
          }
        } catch (error) {
          // Error parsing OIDC session data
        }
      }

      // Fallback: buscar en otras ubicaciones comunes
      return localStorage.getItem('auth_token') ||
        sessionStorage.getItem('auth_token') ||
        localStorage.getItem('access_token') ||
        null;
    };

    let token = getTokenFromStorage();

    if (!token) {
      let waitedTime = 0;
      const maxWaitTime = 3000;
      const checkInterval = 200;

      while (waitedTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, checkInterval));
        waitedTime += checkInterval;

        token = getTokenFromStorage();
        if (token) {
          return token;
        }
      }

      if (!token) {
        // No token available after timeout
      }
    }

    return token;
  }

  /**
   * Construye los headers para las peticiones
   * Siempre incluye el token de autorización Bearer
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = await this.getAuthToken();

    if (!token) {
      // No se encontró token de autenticación. La petición se enviará sin Authorization header.
    } else {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Maneja las respuestas de la API
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error: ApiError = {
        message: `HTTP error! status: ${response.status}`,
        status: response.status,
      };

      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.errors = errorData.errors;
      } catch {
        // Si no se puede parsear el JSON, usar el mensaje por defecto
      }

      // Si el error es 401 (Unauthorized), forzar logout
      if (response.status === 401) {
        console.warn('Token inválido o expirado. Forzando logout...');
        this.handleUnauthorized();
      }

      throw error;
    }

    // Intentar parsear el JSON siempre que sea posible
    // Verificar si la respuesta tiene contenido
    const text = await response.text();

    // Si no hay contenido, retornar objeto vacío
    if (!text || text.trim() === '') {
      return {} as T;
    }

    // Intentar parsear como JSON
    try {
      const jsonData = JSON.parse(text);
      return jsonData;
    } catch (error) {
      // Si no es JSON válido, retornar objeto vacío
      return {} as T;
    }
  }

  /**
   * Realiza una petición GET
   * Siempre incluye el header Authorization Bearer
   */
  async get<T>(endpoint: string): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Realiza una petición POST
   * Siempre incluye el header Authorization Bearer
   */
  async post<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async postWithoutAuth<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    console.log('[apiService.postWithoutAuth] URL:', url);
    console.log('[apiService.postWithoutAuth] Data:', data);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    console.log('[apiService.postWithoutAuth] Response status:', response.status);

    return this.handleResponse<T>(response);
  }

  /**
   * Realiza una petición PUT
   * Siempre incluye el header Authorization Bearer
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Realiza una petición PATCH
   * Siempre incluye el header Authorization Bearer
   */
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Realiza una petición DELETE
   * Siempre incluye el header Authorization Bearer
   */
  async delete<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Descarga un archivo como Blob
   * @param endpoint Endpoint del API
   * @returns Promise con el Blob descargado
   */
  async getBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    const allHeaders = await this.getHeaders();
    // Remover Content-Type para descargas de archivos
    // getHeaders() siempre retorna un objeto Record<string, string>
    const headersObj = allHeaders as Record<string, string>;
    const blobHeaders: Record<string, string> = {};
    Object.keys(headersObj).forEach(key => {
      if (key.toLowerCase() !== 'content-type') {
        blobHeaders[key] = headersObj[key];
      }
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: blobHeaders,
    });

    if (!response.ok) {
      const error: ApiError = {
        message: `HTTP error! status: ${response.status}`,
        status: response.status,
      };

      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.errors = errorData.errors;
      } catch {
        // Si no se puede parsear el JSON, usar el mensaje por defecto
      }

      // Si el error es 401 (Unauthorized), forzar logout
      if (response.status === 401) {
        console.warn('Token inválido o expirado. Forzando logout...');
        this.handleUnauthorized();
      }

      throw error;
    }

    return response.blob();
  }

  /**
   * Descarga un archivo como Blob usando POST
   * @param endpoint Endpoint del API
   * @param body Cuerpo de la petición (opcional)
   * @returns Promise con el Blob descargado
   */
  async postBlob(endpoint: string, body?: any): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    const allHeaders = await this.getHeaders();
    // Remover Content-Type para descargas de archivos
    // getHeaders() siempre retorna un objeto Record<string, string>
    const headersObj = allHeaders as Record<string, string>;
    const blobHeaders: Record<string, string> = {};
    Object.keys(headersObj).forEach(key => {
      if (key.toLowerCase() !== 'content-type') {
        blobHeaders[key] = headersObj[key];
      }
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: blobHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error: ApiError = {
        message: `HTTP error! status: ${response.status}`,
        status: response.status,
      };

      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.errors = errorData.errors;
      } catch {
        // Si no se puede parsear el JSON, usar el mensaje por defecto
      }

      // Si el error es 401 (Unauthorized), forzar logout
      if (response.status === 401) {
        console.warn('Token inválido o expirado. Forzando logout...');
        this.handleUnauthorized();
      }

      throw error;
    }

    return response.blob();
  }

  /**
 * Realiza una petición GET y retorna el texto plano (no JSON)
 * Útil para obtener HTML, XML, o cualquier contenido de texto
 */
  async getText(endpoint: string): Promise<string> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error: ApiError = {
        message: `HTTP error! status: ${response.status}`,
        status: response.status,
      };

      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.errors = errorData.errors;
      } catch {
        // Si no se puede parsear el JSON, usar el mensaje por defecto
      }

      // Si el error es 401 (Unauthorized), forzar logout
      if (response.status === 401) {
        console.warn('Token inválido o expirado. Forzando logout...');
        this.handleUnauthorized();
      }

      throw error;
    }

    // Retornar como texto plano
    return response.text();
  }
}

// Instancia singleton del servicio API
export const apiService = new ApiService();

